import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { AUTH_ME_URL } from '../config/api';
import { clearCsrfToken, refetchCsrfToken } from '../config/csrf';

/* Session state for the whole SPA, backed by the backend's own Django
   session cookie — there's no token to store client-side (BACKEND_API_FLOW.md
   §1). GET /auth/me on mount finds out who (if anyone) the cookie belongs
   to; call refresh() again after the /auth/login redirect round-trip lands
   back here, since the cookie may have just been set/cleared.

   status is one of:
     'loading'       — the /auth/me request hasn't resolved yet
     'authenticated' — 200 back, `user` is populated
     'anonymous'     — 401/403 back, or the request itself failed (e.g.
                       backend unreachable) — treated the same as "not signed
                       in" since there's nothing else useful to show either
                       way. The backend answers a missing/expired session
                       (and a deactivated account) with 403, not 401
                       (BrandonUserStoryAPI.md's no-session error examples);
                       /auth/me has no other reason to 403, so both mean
                       "nobody signed in" here, not an error worth logging */
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [state, setState] = useState({ status: 'loading', user: null });

  const refresh = useCallback(() => {
    setState((s) => ({ ...s, status: 'loading' }));
    fetch(AUTH_ME_URL, { credentials: 'include' })
      .then((res) => {
        if (res.status === 401 || res.status === 403) return null;
        if (!res.ok) throw new Error(`GET /auth/me failed: ${res.status}`);
        return res.json();
      })
      .then((user) => {
        setState({ status: user ? 'authenticated' : 'anonymous', user });
        if (user) refetchCsrfToken();
        else clearCsrfToken();
      })
      .catch((err) => {
        console.error('auth/me check failed', err);
        setState({ status: 'anonymous', user: null });
        clearCsrfToken();
      });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <AuthContext.Provider value={{ ...state, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
